import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Loader2, RefreshCw, Store, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export const StaleOffers = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/market/admin/stale").then(({ data: d }) => setData(d)).catch(() => setData({ listings: [], total_open: 0 }));
  useEffect(() => { load(); }, []);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); toast.success(okMsg); await load(); } catch (e) { toast.error(e?.response?.data?.detail || t("stale.failed")); } finally { setBusy(""); }
  };
  const remindAll = () => run("all", async () => {
    const { data: r } = await api.post("/market/admin/remind-stale");
    toast.info(t("stale.ran").replace("{n}", r.reminded));
  }, t("stale.done"));
  const remind = (l) => run(`r-${l.id}`, () => api.post(`/market/admin/listings/${l.id}/remind`), t("stale.reminded"));
  const close = (l) => run(`c-${l.id}`, () => api.patch(`/market/listings/${l.id}`, { status: l.type === "sell" ? "sold" : "bought" }), t(`market.${l.type === "sell" ? "sold" : "bought"}`));
  const remove = (l) => {
    if (!window.confirm(t("market.delete_title"))) return;
    run(`d-${l.id}`, () => api.delete(`/market/listings/${l.id}`), t("market.deleted"));
  };

  return (
    <section className="space-y-3" data-testid="stale-offers">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-heading text-xl font-semibold text-espresso flex items-center gap-2"><Store className="w-5 h-5 text-olive" />{t("stale.title")}</h2>
          <p className="text-xs text-espresso/60">{data ? t("stale.sub").replace("{n}", data.listings.length).replace("{o}", data.total_open) : t("stale.sub_loading")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1" data-testid="stale-refresh"><RefreshCw className="w-3.5 h-3.5" />{t("common.refresh")}</Button>
        <Button size="sm" onClick={remindAll} disabled={busy === "all"} className="gap-1" data-testid="stale-remind-all">
          {busy === "all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}{t("stale.run_now")}
        </Button>
      </div>
      {data === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
      ) : data.listings.length === 0 ? (
        <div className="text-sm text-espresso/50 bg-white rounded-xl border border-app-border p-5 text-center" data-testid="stale-empty">{t("stale.empty")}</div>
      ) : (
        <div className="bg-white rounded-xl border border-app-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-espresso/60 bg-cream-2">
              <tr>
                <th className="text-start px-3 py-2">{t("market.offer_title")}</th>
                <th className="text-start px-3 py-2">{t("stale.owner")}</th>
                <th className="text-start px-3 py-2">{t("stale.age")}</th>
                <th className="text-start px-3 py-2">{t("stale.last_reminded")}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.listings.map((l) => (
                <tr key={l.id} className="border-t border-app-border/60" data-testid={`stale-row-${l.id}`}>
                  <td className="px-3 py-2 min-w-[180px]">
                    <Link to={`/market/listing/${l.id}`} className="font-semibold text-espresso hover:text-olive">{l.title}</Link>
                    <div className="text-[11px] text-espresso/60">{t(l.type === "sell" ? "market.selling" : "market.buying")} · {t(`market.species_${l.species}`)}</div>
                  </td>
                  <td className="px-3 py-2 min-w-[160px]">
                    <div className="text-espresso">{l.owner_name}</div>
                    <div className="text-[11px] text-espresso/60">{l.owner_email}</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap"><span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">{t("market.age_days").replace("{n}", l.age_days)}</span></td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-espresso/70">
                    {l.last_reminder_at ? `${new Date(l.last_reminder_at).toLocaleDateString()} (×${l.reminder_count})` : t("stale.never")}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      <Button size="sm" variant="outline" onClick={() => remind(l)} disabled={!!busy} className="h-7 text-xs gap-1" data-testid={`stale-remind-${l.id}`}><BellRing className="w-3 h-3" />{t("stale.remind")}</Button>
                      <Button size="sm" onClick={() => close(l)} disabled={!!busy} className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid={`stale-close-${l.id}`}>{t(l.type === "sell" ? "market.mark_sold" : "market.mark_bought")}</Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(l)} disabled={!!busy} className="h-7 text-xs text-red-700 gap-1" data-testid={`stale-delete-${l.id}`}><Trash2 className="w-3 h-3" />{t("common.delete")}</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
